import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Team } from '../models/team';
import { map } from 'rxjs/operators';
import { TeamUser, TeamUserRole } from '../models/team-user';
import { User } from '../models/user';
import { TeamInvite } from '../models/team-invite';
import { TeamInviteExternal } from '../models/team-invite-external';
import { Poll } from '../models/poll';
import { VolleyballStatistic } from '../models/volleyball-statistic';
import { Game } from '../models/game';
import { TeamEvent } from '../models/team-event';
import { Tournament } from '../models/tournament';
import { TournamentTeam } from '../models/tournament-team';
import { TournamentInvite } from '../models/tournament-invite';
import { PaginatedResponse } from './paginated-response.interface';
import { BasketballStatistic } from '../models/basketball-statistic';
import { TeamPermission } from '../models/team-permission';
import { TeamAccess } from '../models/team-access';
import { ConfigService } from '../services/config.service';

@Injectable({providedIn: 'root'})
export class TeamApi {
  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
  ) {}

  async search(query: string, sportId?: number): Promise<Team[]> {
    let params = (new HttpParams()).set('search', query);
    if (sportId) {
      params = params.set('sport', sportId.toString());
    }
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/`, {params})
      .pipe(map(data => Team.toFront(data)))
      .toPromise();
  }

  async create(team: Team): Promise<Team> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team/`, Team.toBack(team))
      .pipe(map(data => Team.toFront(data)))
      .toPromise();
  }

  async getById(teamId: number): Promise<Team> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/`)
      .pipe(map(data => Team.toFront(data)))
      .toPromise();
  }

  async getTeamAccess(teamId: number): Promise<TeamAccess> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/access/`)
      .pipe(map(data => TeamAccess.toFront(data)))
      .toPromise();
  }

  async update(teamId: number, values: any): Promise<Team> {
    return this.httpClient
      .patch(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/`, Team.toBack(values))
      .pipe(map(data => Team.toFront(data)))
      .toPromise();
  }

  async delete(team: Team): Promise<any> {
    return this.httpClient
      .delete(`${this.configService.get('apiUrl')}/api/v1/team/${team.id}/`)
      .toPromise();
  }

  async getPermission(teamId: number): Promise<TeamPermission> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/permission/`)
      .pipe(map(data => TeamPermission.toFront(data)))
      .toPromise();
  }

  async updatePermission(teamId: number, permission: TeamPermission): Promise<TeamPermission> {
    return this.httpClient
      .patch(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/permission/`, TeamPermission.toBack(permission))
      .pipe(map(data => TeamPermission.toFront(data)))
      .toPromise();
  }

  async getUsers(teamId: number, tournamentId?: number): Promise<TeamUser[]> {
    let params = new HttpParams();
    if (tournamentId) {
      params = params.set('tournament_id', tournamentId.toString());
    }
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/users/`, {params})
      .pipe(map(data => TeamUser.toFront(data)))
      .toPromise();
  }

  async updateUserRole(teamId: number, teamUserId: number, role: TeamUserRole): Promise<TeamUser> {
    return this.httpClient
      .patch(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/users/${teamUserId}/role/`, {role: TeamUserRole[role]})
      .pipe(map(data => TeamUser.toFront(data)))
      .toPromise();
  }

  async updateUserNumber(teamId: number, teamUserId: number, gameNumber: number): Promise<TeamUser> {
    return this.httpClient
      .patch(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/users/${teamUserId}/number/`, {number: gameNumber.toString()})
      .pipe(map(data => TeamUser.toFront(data)))
      .toPromise();
  }

  async deleteUser(teamUser: TeamUser): Promise<any> {
    return this.httpClient
      .delete(`${this.configService.get('apiUrl')}/api/v1/team/${teamUser.teamId}/users/${teamUser.id}/`)
      .toPromise();
  }

  async changeOwner(teamId: number, user: User): Promise<Team> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/owner/`, {id: user.id})
      .pipe(map(data => Team.toFront(data)))
      .toPromise();
  }

  async getInvites(teamId: number): Promise<TeamInvite[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/invites/`)
      .pipe(map(data => TeamInvite.toFront(data)))
      .toPromise();
  }

  async sendInvite(teamId: number, user: User): Promise<TeamInvite> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/invites/`, {user})
      .pipe(map(data => TeamInvite.toFront(data)))
      .toPromise();
  }

  async acceptInvite(invite: TeamInvite): Promise<TeamInvite> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team_invite/${invite.id}/accept/`, {})
      .pipe(map(data => TeamInvite.toFront(data)))
      .toPromise();
  }

  async declineInvite(invite: TeamInvite): Promise<TeamInvite> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team_invite/${invite.id}/decline/`, {})
      .pipe(map(data => TeamInvite.toFront(data)))
      .toPromise();
  }

  async resendInvite(invite: TeamInvite): Promise<TeamInvite> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team_invite/${invite.id}/resend/`, {})
      .pipe(map(data => TeamInvite.toFront(data)))
      .toPromise();
  }

  async getInvitesExternal(teamId: number): Promise<TeamInviteExternal[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/invites_external/`)
      .pipe(map(data => TeamInviteExternal.toFront(data)))
      .toPromise();
  }

  async sendInviteExternal(teamId: number, invite: TeamInviteExternal): Promise<TeamInviteExternal> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/invites_external/`, TeamInviteExternal.toBack(invite))
      .pipe(map(data => TeamInviteExternal.toFront(data)))
      .toPromise();
  }

  async deleteInviteExternal(invite: TeamInviteExternal): Promise<any> {
    return this.httpClient
      .delete(`${this.configService.get('apiUrl')}/api/v1/invites_external/${invite.id}/`)
      .toPromise();
  }

  async resendInviteExternal(invite: TeamInviteExternal): Promise<TeamInviteExternal> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/invites_external/${invite.id}/resend/`, {})
      .pipe(map(data => TeamInviteExternal.toFront(data)))
      .toPromise();
  }

  async getPolls(teamId: number, archive: boolean = false): Promise<Poll[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/polls/${archive ? '?archive=1' : ''}`)
      .pipe(map(data => Poll.toFront(data)))
      .toPromise();
  }

  async createPoll(teamId: number, poll: Poll): Promise<Poll> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/polls/`, Poll.toBack(poll))
      .pipe(map(data => Poll.toFront(data)))
      .toPromise();
  }

  async getUsersStatistic(teamId: number): Promise<BasketballStatistic[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/statistics/`)
      .pipe(map(data => BasketballStatistic.toFront(data)))
      .toPromise();
  }

  async getVolleyballStatistic(teamId: number): Promise<VolleyballStatistic[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/volleyball_statistics/`)
      .pipe(map(data => VolleyballStatistic.toFront(data)))
      .toPromise();
  }

  async joinTeam(teamId: number): Promise<TeamInvite> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/join/`, {})
      .pipe(map(data => TeamInvite.toFront(data)))
      .toPromise();
  }

  async leaveTeam(teamId: number): Promise<any> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/leave/`, {})
      .toPromise();
  }

  async getGames(teamId: number, archive = false): Promise<Game[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/games/${archive ? '?archive=1' : ''}`)
      .pipe(map(data => Game.toFront(data)))
      .toPromise();
  }

  async createGame(teamId: number, game: Game): Promise<Game> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/games/`, Game.toBack(game))
      .pipe(map(data => Game.toFront(data)))
      .toPromise();
  }

  async getTeamEvents(teamId: number): Promise<TeamEvent[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/events/`)
      .pipe(map(data => TeamEvent.toFront(data)))
      .toPromise();
  }

  async createTeamEvent(teamId: number, teamEvent: TeamEvent): Promise<TeamEvent> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/events/`, TeamEvent.toBack(teamEvent))
      .pipe(map(data => TeamEvent.toFront(data)))
      .toPromise();
  }

  async getTournaments(teamId: number): Promise<Tournament[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/tournaments/`)
      .pipe(map(data => Tournament.toFront(data)))
      .toPromise();
  }

  async getTournamentTeams(teamId: number): Promise<TournamentTeam[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/tournament_teams/`)
      .pipe(map(data => TournamentTeam.toFront(data)))
      .toPromise();
  }

  async getTournamentInvites(teamId: number): Promise<TournamentInvite[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/tournament_invites/`)
      .pipe(map(data => TournamentInvite.toFront(data)))
      .toPromise();
  }

  async getTournamentGames(teamId: number, tournamentId: number, page: number, size?: number): Promise<PaginatedResponse<Game[]>> {
    let params = new HttpParams().set('page', page.toString());
    if (size) {
      params = params.set('size', size.toString());
    }
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/tournaments/${tournamentId}/games/`, {params, observe: 'response'})
      .pipe(map((response: HttpResponse<any>) => {
        return {
          total: +response.headers.get('X-Page-Count'),
          data: Game.toFront(response.body)
        };
      }))
      .toPromise();
  }

  async acceptTournamentInvite(inviteId: number): Promise<TournamentInvite> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/tournament_invite/${inviteId}/accept/`, {})
      .pipe(map(data => TournamentInvite.toFront(data)))
      .toPromise();
  }

  async declineTournamentInvite(inviteId: number): Promise<TournamentInvite> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/tournament_invite/${inviteId}/decline/`, {})
      .pipe(map(data => TournamentInvite.toFront(data)))
      .toPromise();
  }

  async registerLazyUser(teamId: number, userData: User): Promise<TeamUser> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/team/${teamId}/lazy_user/`,  {user: User.toBack(userData)})
      .pipe(map(data => TeamUser.toFront(data)))
      .toPromise();
  }

  async getLazyUser(teamUserId: number): Promise<TeamUser> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/lazy_user/${teamUserId}/`)
      .pipe(map(data => TeamUser.toFront(data)))
      .toPromise();
  }

  async updateLazyUser(teamUserId: number, userData: User): Promise<TeamUser> {
    return this.httpClient
      .patch(`${this.configService.get('apiUrl')}/api/v1/lazy_user/${teamUserId}/`, {user: User.toBack(userData)})
      .pipe(map(data => TeamUser.toFront(data)))
      .toPromise();
  }

  async deleteLazyUser(teamUserId: number): Promise<any> {
    return this.httpClient
      .delete(`${this.configService.get('apiUrl')}/api/v1/lazy_user/${teamUserId}/`)
      .toPromise();
  }

  async resendLazyUserEmail(teamUserId: number): Promise<any> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/lazy_user/${teamUserId}/resend_email/`, {})
      .toPromise();
  }
}
